import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  OrderedListOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined
} from "@ant-design/icons";
import {
  Button,
  Card,
  Descriptions,
  Grid,
  Layout,
  List,
  Select,
  Typography,
  Space,
  Popconfirm,
  Tag,
  Divider,
  Row,
  Col
} from "antd";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";

import CourseContestsManagementModal from "./ContestManagementModal";
import CourseContestLinkModal from "./CourseContestLinkModal";
import { showErrorNotification, showSuccessNotification } from "../../../utils/notification";
import { clamp } from "../../../utils/responsiveHelpers";
import LinkedEntityField from "../LinkedEntityField";

const { Content } = Layout;
const { Title, Text } = Typography;

const CONTEST_LINKING_PATH = "admin/courses/manage/contest-linking";

const CourseContestManagementDashboard = ({ ROOT_URL }) => {
  const [courses, setCourses] = useState([]);
  const [availableContests, setAvailableContests] = useState([]);
  const [contestsByCourse, setContestsByCourse] = useState({});
  const [modulesByCourse, setModulesByCourse] = useState({});
  const [selectedCourseUniquePublicId, setSelectedCourseUniquePublicId] = useState(null);

  const [isCourseContestLinkModalOpen, setIsCourseContestLinkModalOpen] = useState(false);
  const [isContestManagementModalOpen, setIsContestManagementModalOpen] = useState(false);
  const [activeLinkData, setActiveLinkData] = useState(null);
  const [loading, setLoading] = useState(false);

  const screens = Grid.useBreakpoint();
  const isMobileView = screens.lg === false;

  const getApiUrl = (endpoint) => `${ROOT_URL}/${CONTEST_LINKING_PATH}${endpoint}`;

  const selectedCourseContests = useMemo(() => {
    if (!selectedCourseUniquePublicId) {
      return [];
    }
    return contestsByCourse[selectedCourseUniquePublicId] || [];
  }, [contestsByCourse, selectedCourseUniquePublicId]);

  const selectedCourseModules = useMemo(() => {
    if (!selectedCourseUniquePublicId) {
      return [];
    }
    return modulesByCourse[selectedCourseUniquePublicId] || [];
  }, [modulesByCourse, selectedCourseUniquePublicId]);

  const fetchPrerequisitesData = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(getApiUrl("/prerequisites"));
      const {
        courses = [],
        availableContests = [],
        contestsByCourse = {},
        modulesByCourse = {}
      } = data || {};

      setCourses(courses);
      setAvailableContests(availableContests);
      setContestsByCourse(contestsByCourse);
      setModulesByCourse(modulesByCourse);

      if (!selectedCourseUniquePublicId && courses.length) {
        setSelectedCourseUniquePublicId(courses[0].uniquePublicId);
      }
    } catch (error) {
      showErrorNotification(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlink = async (uniquePublicId) => {
    try {
      await axios.post(getApiUrl("/unlink-contest"), {
        courseUniquePublicId: selectedCourseUniquePublicId,
        contestUniquePublicId: uniquePublicId
      });
      showSuccessNotification("Contest unlinked");
      fetchPrerequisitesData();
    } catch (error) {
      showErrorNotification(error);
    }
  };

  useEffect(() => {
    fetchPrerequisitesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getModuleTitle = (moduleId) => {
    const module = selectedCourseModules.find(
      (moduleDetails) => moduleDetails.uniquePublicId === moduleId
    );
    return module ? module.title : "Unknown Module";
  };

  /**
   * Edit and unlink controls for a linked contest. Rendered as List.Item actions on
   * desktop, and in the card header on mobile.
   *
   * @param {Object} contestLink - Linked contest row.
   * @returns {Array<React.ReactNode>}
   */
  const renderContestLinkActions = (contestLink) => [
    <Button
      key="edit"
      size="small"
      icon={<EditOutlined />}
      onClick={() => {
        setActiveLinkData(contestLink);
        setIsCourseContestLinkModalOpen(true);
      }}
    />,
    <Popconfirm
      key="unlink"
      title="Unlink Contest?"
      onConfirm={() => handleUnlink(contestLink.uniquePublicId)}
      okText="Yes"
      cancelText="No"
      getPopupContainer={(node) => node.parentElement}
    >
      <Button size="small" danger icon={<DeleteOutlined />} />
    </Popconfirm>
  ];

  return (
    <Layout>
      <Content style={{ padding: clamp(12, 24) }}>
        <Card style={{ minHeight: "calc(100dvh - 100px)" }}>
          <Space
            wrap
            style={{
              width: "100%",
              justifyContent: "space-between",
              alignItems: "flex-end"
            }}
          >
            <Space direction="vertical" size={4}>
              <Title level={4} style={{ marginBottom: 0 }}>
                Course → Contest Linking
              </Title>
              <Text type="secondary">Manage contest visibility, order and prerequisites</Text>

              <Space style={{ marginTop: 8 }}>
                <Text strong>Active Course:</Text>
                <Select
                  style={{ width: "min(260px, 100%)" }}
                  value={selectedCourseUniquePublicId}
                  onChange={setSelectedCourseUniquePublicId}
                  placeholder="Select course"
                  showSearch
                  options={courses.map((courseDetails) => ({
                    label: courseDetails.name,
                    value: courseDetails.uniquePublicId
                  }))}
                />
              </Space>
            </Space>

            <Space>
              <Button onClick={() => setIsContestManagementModalOpen(true)}>Manage Contests</Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                disabled={!selectedCourseUniquePublicId}
                onClick={() => {
                  setActiveLinkData(null);
                  setIsCourseContestLinkModalOpen(true);
                }}
              >
                Link Contest
              </Button>
            </Space>
          </Space>

          <Divider />

          <List
            loading={loading}
            // Mobile rows are already bordered cards, so the list divider is just noise
            split={!isMobileView}
            dataSource={selectedCourseContests}
            locale={{ emptyText: "No contests linked to this course" }}
            rowKey="uniquePublicId"
            renderItem={(item, index) => {
              const { title, name, displayOrder, settings, preRequisitesText, prerequisites } =
                item;
              const { isVisible } = settings ?? {};

              const orderValue = (
                <Text>
                  <OrderedListOutlined style={{ marginRight: 4, fontSize: 12 }} />
                  {displayOrder}
                </Text>
              );

              const visibleValue = (
                <Text>
                  {isVisible ? (
                    <>
                      <EyeOutlined style={{ color: "#52C41A", marginRight: 4 }} /> Yes
                    </>
                  ) : (
                    <>
                      <EyeInvisibleOutlined style={{ color: "#FF4D4F", marginRight: 4 }} /> No
                    </>
                  )}
                </Text>
              );

              const accessRequirementsValue = (
                <Space direction="vertical" size={4} style={{ marginTop: 2 }}>
                  {preRequisitesText && (
                    <Text
                      ellipsis={{ tooltip: preRequisitesText }}
                      style={{ fontSize: "12px", fontStyle: "italic" }}
                    >
                      &quot;{preRequisitesText}&quot;
                    </Text>
                  )}
                  <Space wrap size={[4, 4]}>
                    {prerequisites?.length > 0 ? (
                      prerequisites.map(({ moduleUniquePublicId, isRequired }) => (
                        <Tag
                          key={moduleUniquePublicId}
                          icon={
                            isRequired ? (
                              <CheckCircleOutlined style={{ color: "#52C41A" }} />
                            ) : (
                              <InfoCircleOutlined />
                            )
                          }
                          style={{
                            borderRadius: "4px",
                            fontSize: "11px",
                            marginInlineEnd: 0,
                            whiteSpace: "normal"
                          }}
                        >
                          {getModuleTitle(moduleUniquePublicId)}
                        </Tag>
                      ))
                    ) : (
                      <Text type="disabled" style={{ fontSize: "12px" }}>
                        No module dependencies
                      </Text>
                    )}
                  </Space>
                </Space>
              );

              // Access requirements can hold several wrapped tags, so on a phone the row becomes a
              // self-contained card with an aligned label/value table instead of a squeezed grid.
              if (isMobileView) {
                return (
                  <List.Item style={{ padding: "8px 0" }}>
                    <Card
                      size="small"
                      style={{ width: "100%" }}
                      styles={{ title: { whiteSpace: "normal" }, body: { padding: 0 } }}
                      title={
                        <Space size={8} wrap>
                          <Text strong>
                            {index + 1}. {title || name}
                          </Text>
                          {!isVisible && <Tag icon={<EyeInvisibleOutlined />}>Hidden</Tag>}
                        </Space>
                      }
                      extra={<Space size={4}>{renderContestLinkActions(item)}</Space>}
                    >
                      <Descriptions
                        bordered
                        size="small"
                        column={1}
                        styles={{
                          label: { width: 96, whiteSpace: "nowrap" },
                          content: { wordBreak: "break-word" }
                        }}
                        items={[
                          { key: "order", label: "Order", children: orderValue },
                          { key: "visible", label: "Visible", children: visibleValue },
                          {
                            key: "access",
                            label: "Access",
                            children: accessRequirementsValue
                          }
                        ]}
                      />
                    </Card>
                  </List.Item>
                );
              }

              return (
                <List.Item style={{ padding: "16px 0" }} actions={renderContestLinkActions(item)}>
                  <Row align="middle" style={{ width: "100%" }} gutter={[16, 12]}>
                    <Col lg={6}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Text strong style={{ fontSize: "15px" }}>
                          {index + 1}. {title || name}
                        </Text>
                        {!isVisible && <Tag icon={<EyeInvisibleOutlined />}>Hidden</Tag>}
                      </div>
                    </Col>

                    <Col lg={2}>
                      <LinkedEntityField label="Order">{orderValue}</LinkedEntityField>
                    </Col>

                    <Col lg={4}>
                      <LinkedEntityField label="Visible to Students">
                        {visibleValue}
                      </LinkedEntityField>
                    </Col>

                    <Col lg={12}>
                      <LinkedEntityField label="Access Requirements">
                        {accessRequirementsValue}
                      </LinkedEntityField>
                    </Col>
                  </Row>
                </List.Item>
              );
            }}
          />
        </Card>
      </Content>

      <CourseContestLinkModal
        isOpen={isCourseContestLinkModalOpen}
        activeLinkData={activeLinkData}
        onCancel={() => setIsCourseContestLinkModalOpen(false)}
        onSuccess={() => {
          setIsCourseContestLinkModalOpen(false);
          fetchPrerequisitesData();
        }}
        getApiUrl={getApiUrl}
        courseUniquePublicId={selectedCourseUniquePublicId}
        availableContests={availableContests}
        linkedContests={selectedCourseContests}
        courseModules={selectedCourseModules}
      />
      {isContestManagementModalOpen && (
        <CourseContestsManagementModal
          isModalOpen={isContestManagementModalOpen}
          setIsModalOpen={setIsContestManagementModalOpen}
          ROOT_URL={ROOT_URL}
        />
      )}
    </Layout>
  );
};

export default CourseContestManagementDashboard;
